import React, { useState, useEffect } from "react";
import { showToast } from "../../Shared/Toaster";
import api from "../../../api/axiosConfig";
import "../../../css/EditStationModal.css";

// Use the same Role interface from your AddStationModal
interface Role {
  _id: string;
  name: string;
  parent: string | null;
}

// Re-using the Station interface from your main component
interface Station {
  id: string;
  type: string;
  description?: string;
  status: "active" | "inactive";
  role: string;      // This is role ID
  subrole: string;   // This is subrole ID
}

interface EditStationModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: Station | null;
  onSuccess: () => void;
}

const EditStationModal: React.FC<EditStationModalProps> = ({
  isOpen,
  onClose,
  station,
  onSuccess,
}) => {
  // Form state
  const [stationName, setStationName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedSubroleId, setSelectedSubroleId] = useState("");

  // State for dropdown options
  const [roles, setRoles] = useState<Role[]>([]);
  const [subroles, setSubroles] = useState<Role[]>([]);
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ 1. Pre-populate the form when the `station` prop is available
  useEffect(() => {
    if (station) {
      setStationName(station.type || "");
      setDescription(station.description || "");
      setIsActive(station.status === "active");
      setSelectedRoleId(station.role);
      setSelectedSubroleId(station.subrole || "");
    }
  }, [station]);

  // ✅ 2. Fetch top-level roles when the modal opens
  useEffect(() => {
    const fetchRoles = async () => {
      if (!isOpen) return;
      try {
        const token = sessionStorage.getItem("accessToken");
        // NOTE: Adjust the API instance (`auth_api` or `center_api`) and endpoint as needed
        const res = await api.auth_api.get("/roles", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const topLevelRoles = res.data.filter((role: Role) => role.parent === null);
        setRoles(topLevelRoles);
      } catch (err) {
        console.error("❌ Error fetching roles:", err);
        setError("Could not load roles.");
      }
    };
    fetchRoles();
  }, [isOpen]);

  // ✅ 3. Fetch sub-roles whenever the selected role changes
  useEffect(() => {
    const fetchSubroles = async () => {
      if (!selectedRoleId) {
        setSubroles([]);
        return;
      }
      try {
        const token = sessionStorage.getItem("accessToken");
        // NOTE: Adjust the API instance and endpoint as needed
        const res = await api.auth_api.get(`/roles/${selectedRoleId}/subroles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubroles(res.data || []);
      } catch (err) {
        console.error("❌ Error fetching subroles:", err);
      }
    };
    fetchSubroles();
  }, [selectedRoleId]);

  // ✅ 4. Handler to reset sub-role when the main role is changed
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRoleId(e.target.value);
    setSelectedSubroleId(""); // Reset sub-role so the user must re-select
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!station) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const token = sessionStorage.getItem("accessToken");
      
      const selectedRole = roles.find((role) => role._id === selectedRoleId);
      
      // Start with the base payload
      const payload: any = {
        stationId: station.id,
        name: stationName,
        description,
        role: selectedRoleId,
        roleName: selectedRole?.name,
        // isActive,
      };

      // Conditionally add sub-role data if selected
      if (selectedSubroleId) {
        const selectedSubrole = subroles.find(sub => sub._id === selectedSubroleId);
        payload.subrole = selectedSubroleId;
        payload.subroleName = selectedSubrole?.name;
      }

      await api.center_api.put('/api/v1/stations/update', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      showToast("Station updated successfully ✅", "success");
      onSuccess();
      onClose();

    } catch (err: any) {
      console.error("Error updating station:", err);
      const errMsg = err.response?.data?.message || "Failed to save changes.";
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !station) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="modal-header"><h2>Edit Station</h2>
            <button className="global-close-btn" onClick={onClose}>✕</button>
        </div>
          <div className="modal-body form-body">
            <div className="form-group">
              <label>Station Name</label>
              <input type="text" value={stationName} onChange={(e) => setStationName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="role">Assign Role</label>
              <select id="role" value={selectedRoleId} onChange={handleRoleChange} required>
                <option value="">Select a role</option>
                {roles.map((role) => (<option key={role._id} value={role._id}>{role.name}</option>))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="subrole">Assign Sub-Role (Optional)</label>
              <select id="subrole" value={selectedSubroleId} onChange={(e) => setSelectedSubroleId(e.target.value)} disabled={!selectedRoleId || subroles.length === 0}>
                <option value="">Select a Sub-role</option>
                {subroles.map((subrole) => (<option key={subrole._id} value={subrole._id}>{subrole.name}</option>))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <input
                id="status"
                type="text"
                value={isActive ? "Active" : "Inactive"}
                readOnly
                disabled
                className="form-input"
              />
            </div>
            {error && <p className="error-message">{error}</p>}
          </div>
          <div className="modal-footer form-footer">
            <button type="submit" className="save-btn" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStationModal;